<template>
  <div class="post-page" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!$props.embedded" class="page-title">动态</h2>

      <!-- Tab 切换 -->
      <div v-if="!$props.embedded" class="tab-bar">
        <div
          class="tab-item"
          :class="{ active: currentTab === 'mine' }"
          @click="switchTab('mine')"
        >我的动态</div>
        <div
          class="tab-item"
          :class="{ active: currentTab === 'feed' }"
          @click="switchTab('feed')"
        >关注动态</div>
      </div>

      <!-- 发布动态（仅我的动态 tab 显示） -->
      <div v-if="currentTab === 'mine'" class="post-form">
        <textarea
          v-model="postForm.content"
          class="post-textarea"
          placeholder="分享你的想法..."
          rows="3"
        ></textarea>
        <div class="post-form-row">
          <input
            v-model="postForm.images"
            class="post-input"
            placeholder="图片链接（多张用逗号分隔）"
          >
        </div>
        <div class="post-form-row">
          <input
            v-model="postForm.video_id"
            class="post-input short"
            placeholder="关联视频ID（可选）"
          >
          <button
            class="post-submit"
            :disabled="postSubmitting"
            @click="submitPost"
          >
            <svg v-if="!postSubmitting" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
            <span>{{ postSubmitting ? '发布中...' : '发布' }}</span>
          </button>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading && list.length === 0" class="loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 空状态 -->
        <div v-if="list.length === 0" class="empty-tip">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <p class="empty-text">{{ currentTab === 'mine' ? '还没有发布动态' : '关注的人还没有动态' }}</p>
          <p class="empty-sub">{{ currentTab === 'mine' ? '分享你的想法，开始互动吧' : '去发现更多有趣的人' }}</p>
        </div>

        <!-- 动态卡片列表 -->
        <div v-else class="post-list">
          <div
            v-for="item in list"
            :key="item.id"
            class="post-card"
          >
            <!-- 头部：头像 + 昵称 + 时间 -->
            <div class="post-header">
              <img
                :src="item.avatar || defaultAvatar"
                class="post-avatar"
                alt="头像"
              >
              <div class="post-author-info">
                <span class="post-author">{{ item.nickname || item.username }}</span>
                <span class="post-time">{{ formatTime(item.createtime) }}</span>
              </div>
            </div>

            <!-- 文字内容 -->
            <p class="post-content" v-if="item.content">{{ item.content }}</p>

            <!-- 图片展示 -->
            <div
              v-if="item.images"
              class="post-images"
              :class="'count-' + imageCount(item.images)"
            >
              <div
                v-for="(img, idx) in parseImages(item.images)"
                :key="idx"
                class="post-image-item"
                @click="previewImage(parseImages(item.images), idx)"
              >
                <img :src="img" alt="图片">
              </div>
            </div>

            <!-- 关联视频 -->
            <div
              v-if="item.video_id"
              class="post-video-link"
              @click="goVideo(item.video_id)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              <span>查看关联视频</span>
            </div>

            <!-- 底部操作 -->
            <div class="post-actions">
              <button
                class="action-btn like-btn"
                :class="{ liked: item.is_liked }"
                @click="toggleLike(item)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" :fill="item.is_liked ? '#ff4757' : 'none'" :stroke="item.is_liked ? '#ff4757' : '#999'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                <span>{{ item.like_count || 0 }}</span>
              </button>
              <button
                class="action-btn comment-btn"
                :class="{ active: expandedComments[item.id] }"
                @click="toggleComments(item)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>{{ item.comment_count || 0 }}</span>
              </button>
              <button
                v-if="item.is_own"
                class="action-btn delete-btn"
                @click="deletePost(item)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                <span>删除</span>
              </button>
            </div>

            <!-- 评论展开区域 -->
            <div v-if="expandedComments[item.id]" class="comments-section">
              <div class="comments-header">
                <span class="comments-title">评论 {{ item.comment_count || 0 }}</span>
                <span class="comments-close" @click="toggleComments(item)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                  收起
                </span>
              </div>

              <!-- 评论列表 -->
              <div v-if="commentsLoading[item.id] && (!commentsMap[item.id] || commentsMap[item.id].length === 0)" class="comments-loading">
                <div class="comments-loading-spinner"></div>
                <span>加载评论中...</span>
              </div>

              <template v-else>
                <div v-if="!commentsMap[item.id] || commentsMap[item.id].length === 0" class="comments-empty">
                  暂无评论，快来抢沙发吧~
                </div>
                <div v-else class="comments-list">
                  <div
                    v-for="comment in commentsMap[item.id]"
                    :key="comment.id"
                    class="comment-item"
                  >
                    <img
                      :src="comment.avatar || defaultAvatar"
                      class="comment-avatar"
                      alt="头像"
                    >
                    <div class="comment-body">
                      <div class="comment-author-row">
                        <span class="comment-author">{{ comment.nickname || comment.username }}</span>
                        <span v-if="comment.is_own" class="comment-tag-self">我</span>
                      </div>
                      <p class="comment-content">{{ comment.content }}</p>
                      <div class="comment-footer">
                        <span class="comment-time">{{ formatTime(comment.createtime) }}</span>
                        <div class="comment-actions">
                          <button
                            class="comment-action like"
                            :class="{ liked: comment.is_liked }"
                            @click="handleLikeComment(item, comment)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" :fill="comment.is_liked ? '#ff4757' : 'none'" :stroke="comment.is_liked ? '#ff4757' : '#999'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                            <span>{{ comment.like_count || 0 }}</span>
                          </button>
                          <button
                            v-if="comment.is_own"
                            class="comment-action delete"
                            @click="handleDeleteComment(item, comment)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            <span>删除</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 加载更多评论 -->
                <div
                  v-if="commentsHasMore[item.id]"
                  class="comments-load-more"
                  @click="loadMoreComments(item)"
                >
                  <span v-if="commentsLoading[item.id]">加载中...</span>
                  <span v-else>加载更多评论</span>
                </div>
              </template>

              <!-- 评论输入框 -->
              <div class="comment-input-area">
                <input
                  type="text"
                  v-model="commentInput[item.id]"
                  placeholder="发表评论..."
                  @keyup.enter="submitComment(item)"
                  :disabled="commentSubmitting[item.id]"
                >
                <button
                  class="comment-submit-btn"
                  :disabled="commentSubmitting[item.id] || !(commentInput[item.id] && commentInput[item.id].trim())"
                  @click="submitComment(item)"
                >
                  {{ commentSubmitting[item.id] ? '发送中' : '发送' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="hasMore" class="load-more" @click="loadMore">
          <span>加载更多</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <div v-else-if="list.length > 0" class="load-end">
          <span class="load-end-line"></span>
          <span class="load-end-text">没有更多了</span>
          <span class="load-end-line"></span>
        </div>
      </template>
    </div>
    <PageFooter v-if="!$props.embedded" />

    <!-- 图片预览弹窗 -->
    <div
      v-if="previewVisible"
      class="image-preview-overlay"
      @click="previewVisible = false"
    >
      <div class="preview-close" @click="previewVisible = false">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
      <img
        :src="previewImages[previewIndex]"
        class="image-preview-big"
        alt="预览"
      >
      <div class="image-preview-nav">
        <span
          v-if="previewImages.length > 1 && previewIndex > 0"
          class="nav-btn prev"
          @click.stop="previewIndex--"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </span>
        <span class="nav-counter">{{ previewIndex + 1 }} / {{ previewImages.length }}</span>
        <span
          v-if="previewImages.length > 1 && previewIndex < previewImages.length - 1"
          class="nav-btn next"
          @click.stop="previewIndex++"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import * as postApi from '../api/post';
import { getPostComments, addPostComment, deletePostComment, likePostComment, unlikePostComment } from '../api/post';

export default {
  name: 'PostFeed',
  components: { PageFooter },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    void props.embedded;
    const currentTab = ref('mine');
    const list = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const hasMore = ref(true);
    const postSubmitting = ref(false);
    const defaultAvatar = require('../assets/logo.png');

    // 发布表单
    const postForm = ref({ content: '', images: '', video_id: '' });

    // 图片预览
    const previewVisible = ref(false);
    const previewImages = ref([]);
    const previewIndex = ref(0);

    // 评论相关
    const expandedComments = ref({}); // 记录哪些动态展开了评论
    const commentsMap = ref({}); // postId -> comments list
    const commentInput = ref({}); // postId -> input content
    const commentSubmitting = ref({}); // postId -> submitting
    const commentsLoading = ref({}); // postId -> loading
    const commentsHasMore = ref({}); // postId -> hasMore
    const commentsPage = ref({}); // postId -> current page

    const fetchList = async (pageNum = 1, append = false) => {
      loading.value = true;
      try {
        const apiFn = currentTab.value === 'mine' ? postApi.getMyPosts : postApi.getFeed;
        const res = await apiFn({ page: pageNum });
        if (res.code === 1) {
          const data = res.data;
          const items = data.list || data.data || [];
          if (append) {
            list.value = [...list.value, ...items];
          } else {
            list.value = items;
          }
          hasMore.value = items.length >= (data.per_page || 10);
          page.value = pageNum;
        }
      } catch (e) {
        console.error('获取动态失败', e);
      } finally {
        loading.value = false;
      }
    };

    const switchTab = (tab) => {
      if (currentTab.value === tab) return;
      currentTab.value = tab;
      page.value = 1;
      hasMore.value = true;
      fetchList();
    };

    const loadMore = () => {
      if (!hasMore.value || loading.value) return;
      fetchList(page.value + 1, true);
    };

    const submitPost = async () => {
      const content = postForm.value.content.trim();
      if (!content && !postForm.value.images) {
        alert('请输入内容或添加图片');
        return;
      }
      postSubmitting.value = true;
      try {
        const res = await postApi.createPost({
          content: content,
          images: postForm.value.images.trim(),
          video_id: postForm.value.video_id.trim()
        });
        if (res.code === 1) {
          postForm.value = { content: '', images: '', video_id: '' };
          // 插入新动态到列表顶部
          if (res.data) {
            list.value.unshift(res.data);
          } else {
            page.value = 1;
            hasMore.value = true;
            await fetchList();
          }
        } else {
          alert(res.msg || '发布失败');
        }
      } catch (e) {
        alert('发布失败，请重试');
      } finally {
        postSubmitting.value = false;
      }
    };

    const toggleLike = async (item) => {
      try {
        const apiFn = item.is_liked ? postApi.unlikePost : postApi.likePost;
        const res = await apiFn(item.id);
        if (res.code === 1) {
          item.is_liked = Boolean(res.data && res.data.is_liked);
          item.like_count = Number(res.data && res.data.like_count) || 0;
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        console.error('点赞失败', e);
      }
    };

    const deletePost = async (item) => {
      if (!confirm('确定要删除这条动态吗？')) return;
      try {
        const res = await postApi.deletePost(item.id);
        if (res.code === 1) {
          list.value = list.value.filter(i => i.id !== item.id);
        } else {
          alert(res.msg || '删除失败');
        }
      } catch (e) {
        alert('删除失败，请重试');
      }
    };

    const parseImages = (images) => {
      if (!images) return [];
      if (Array.isArray(images)) return images;
      return images.split(',').map(s => s.trim()).filter(Boolean);
    };

    const imageCount = (images) => {
      return parseImages(images).length;
    };

    const previewImage = (images, idx) => {
      previewImages.value = images;
      previewIndex.value = idx;
      previewVisible.value = true;
    };

    const goVideo = (videoId) => {
      window.location.hash = '#/live/' + videoId;
    };

    const goComments = (item) => {
      // 兼容：直接跳转详情页
      window.location.hash = '#/user?tab=posts&post=' + encodeURIComponent(item.id);
    };

    // 切换评论展开/收起
    const toggleComments = async (item) => {
      const postId = item.id;
      if (expandedComments.value[postId]) {
        // 收起
        expandedComments.value[postId] = false;
        return;
      }
      // 展开
      expandedComments.value[postId] = true;
      if (!commentsMap.value[postId] || commentsMap.value[postId].length === 0) {
        await fetchComments(item, 1, false);
      }
    };

    // 拉取评论列表
    const fetchComments = async (item, pageNum = 1, append = false) => {
      const postId = item.id;
      commentsLoading.value[postId] = true;
      try {
        const res = await getPostComments({ post_id: postId, page: pageNum });
        if (res.code === 1) {
          const data = res.data || {};
          const items = data.list || data.data || [];
          if (append) {
            commentsMap.value[postId] = [...(commentsMap.value[postId] || []), ...items];
          } else {
            commentsMap.value[postId] = items;
          }
          commentsHasMore.value[postId] = items.length >= (data.per_page || 10);
          commentsPage.value[postId] = pageNum;
        }
      } catch (e) {
        console.error('获取评论失败', e);
      } finally {
        commentsLoading.value[postId] = false;
      }
    };

    // 加载更多评论
    const loadMoreComments = (item) => {
      const postId = item.id;
      const nextPage = (commentsPage.value[postId] || 1) + 1;
      fetchComments(item, nextPage, true);
    };

    // 发表评论
    const submitComment = async (item) => {
      const postId = item.id;
      const content = (commentInput.value[postId] || '').trim();
      if (!content) {
        alert('请输入评论内容');
        return;
      }
      commentSubmitting.value[postId] = true;
      try {
        const res = await addPostComment({ post_id: postId, content });
        if (res.code === 1) {
          commentInput.value[postId] = '';
          // 更新评论数
          if (typeof item.comment_count === 'number') {
            item.comment_count = (item.comment_count || 0) + 1;
          }
          // 重新加载评论列表
          await fetchComments(item, 1, false);
        } else {
          alert(res.msg || '评论失败');
        }
      } catch (e) {
        alert(e.message || '评论失败');
      } finally {
        commentSubmitting.value[postId] = false;
      }
    };

    // 删除评论
    const handleDeleteComment = async (item, comment) => {
      if (!confirm('确定删除这条评论吗？')) return;
      try {
        const res = await deletePostComment(comment.id);
        if (res.code === 1) {
          // 从列表移除
          const postId = item.id;
          commentsMap.value[postId] = (commentsMap.value[postId] || []).filter(c => c.id !== comment.id);
          // 更新评论数
          if (typeof item.comment_count === 'number' && item.comment_count > 0) {
            item.comment_count = item.comment_count - 1;
          }
        } else {
          alert(res.msg || '删除失败');
        }
      } catch (e) {
        alert(e.message || '删除失败');
      }
    };

    // 点赞评论
    const handleLikeComment = async (item, comment) => {
      try {
        const apiFn = comment.is_liked ? unlikePostComment : likePostComment;
        const res = await apiFn(comment.id);
        if (res.code === 1) {
          comment.is_liked = Boolean(res.data && res.data.is_liked);
          comment.like_count = Number(res.data && res.data.like_count) || 0;
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        console.error('点赞评论失败', e);
      }
    };

    const formatTime = (timestamp) => {
      if (!timestamp) return '';
      if (typeof timestamp === 'string' && !isNaN(Date.parse(timestamp))) {
        const d = new Date(timestamp);
        const now = new Date();
        const diff = Math.floor((now - d) / 1000);
        if (diff < 60) return '刚刚';
        if (diff < 3600) return Math.floor(diff / 60) + '分钟前';
        if (diff < 86400) return Math.floor(diff / 3600) + '小时前';
        if (diff < 259200) return Math.floor(diff / 86400) + '天前';
        return d.toLocaleDateString('zh-CN');
      }
      const num = Number(timestamp);
      if (isNaN(num) || num <= 0) return '';
      const date = new Date(num * 1000);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return '刚刚';
      if (diff < 3600) return Math.floor(diff / 60) + '分钟前';
      if (diff < 86400) return Math.floor(diff / 3600) + '小时前';
      if (diff < 259200) return Math.floor(diff / 86400) + '天前';
      return date.toLocaleDateString('zh-CN');
    };

    onMounted(() => {
      fetchList();
    });

    return {
      currentTab,
      list,
      loading,
      hasMore,
      postForm,
      postSubmitting,
      defaultAvatar,
      previewVisible,
      previewImages,
      previewIndex,
      switchTab,
      loadMore,
      submitPost,
      toggleLike,
      deletePost,
      parseImages,
      imageCount,
      previewImage,
      goVideo,
      goComments,
      formatTime,
      // 评论相关
      expandedComments,
      commentsMap,
      commentInput,
      commentSubmitting,
      commentsLoading,
      commentsHasMore,
      commentsPage,
      toggleComments,
      fetchComments,
      loadMoreComments,
      submitComment,
      handleDeleteComment,
      handleLikeComment
    };
  }
};
</script>

<style scoped>
.post-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f6f7f9 0%, #f0f2f5 100%);
  padding: 40px 0;
}
.post-page.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}
.post-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
.container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}
.page-title {
  font-size: 24px;
  color: #1a1a2e;
  margin-bottom: 24px;
  font-weight: 700;
  position: relative;
  padding-left: 16px;
}
.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

/* Tab 切换 */
.tab-bar {
  display: flex;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.tab-item {
  flex: 1;
  text-align: center;
  padding: 14px 0;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}
.tab-item.active {
  color: #ff9600;
  background: #fff;
}
.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #ff9600, #ff7d00);
  border-radius: 2px;
}
.tab-item:hover:not(.active) {
  color: #ff9600;
  background: #fafafa;
}

/* 发布动态表单 */
.post-form {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.post-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #f8fafc;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s;
}
.post-textarea:focus {
  border-color: #ff9600;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(255,150,0,0.08);
}
.post-form-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
.post-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  box-sizing: border-box;
  transition: all 0.3s;
}
.post-input:focus {
  border-color: #ff9600;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(255,150,0,0.08);
}
.post-input.short {
  flex: 0 1 180px;
}
.post-submit {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(255,106,0,0.2);
}
.post-submit:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}
.post-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,106,0,0.3);
}

/* 动态卡片列表 */
.post-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.post-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
  transition: all 0.3s ease;
}
.post-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.06);
  transform: translateY(-1px);
}

/* 头部 */
.post-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.post-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid #f0f0f0;
}
.post-author-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.post-author {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.post-time {
  font-size: 12px;
  color: #bbb;
}

/* 内容 */
.post-content {
  font-size: 15px;
  color: #333;
  line-height: 1.7;
  margin: 0 0 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 图片网格 */
.post-images {
  display: grid;
  gap: 4px;
  margin-bottom: 14px;
  border-radius: 10px;
  overflow: hidden;
}
.post-images.count-1 {
  grid-template-columns: 1fr;
  max-width: 100%;
}
.post-images.count-2 {
  grid-template-columns: 1fr 1fr;
}
.post-images.count-3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.post-images.count-4 {
  grid-template-columns: 1fr 1fr;
}
.post-images.count-5,
.post-images.count-6 {
  grid-template-columns: 1fr 1fr 1fr;
}
.post-images.count-7,
.post-images.count-8,
.post-images.count-9 {
  grid-template-columns: 1fr 1fr 1fr;
}
.post-image-item {
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
  background: #f0f0f0;
  border-radius: 4px;
}
.post-image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.post-image-item:hover img {
  transform: scale(1.08);
}

/* 关联视频 */
.post-video-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: linear-gradient(135deg, #fff7ed, #fff);
  border: 1px solid #fed7aa;
  border-radius: 10px;
  font-size: 13px;
  color: #ff9600;
  cursor: pointer;
  margin-bottom: 14px;
  transition: all 0.3s;
  font-weight: 500;
}
.post-video-link:hover {
  background: linear-gradient(135deg, #ffedcc, #fff7ed);
  border-color: #ff9600;
}

/* 底部操作 */
.post-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}
.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  font-size: 13px;
  color: #999;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: all 0.3s;
}
.action-btn:hover {
  background: #f5f5f5;
}
.like-btn.liked {
  color: #ff4757;
}
.like-btn.liked svg {
  fill: #ff4757;
  stroke: #ff4757;
}
.like-btn:hover:not(.liked) {
  color: #ff4757;
}
.like-btn:hover:not(.liked) svg {
  stroke: #ff4757;
}
.comment-btn:hover {
  color: #ff9600;
}
.comment-btn:hover svg {
  stroke: #ff9600;
}
.delete-btn:hover {
  color: #ef4444;
  background: rgba(239,68,68,0.06);
}
.delete-btn:hover svg {
  stroke: #ef4444;
}
.comment-btn.active {
  color: #ff9600;
  background: #fff5eb;
}
.comment-btn.active svg {
  stroke: #ff9600;
}

/* 评论展开区域 */
.comments-section {
  margin-top: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
}
.comments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.comments-title {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}
.comments-close {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #999;
  cursor: pointer;
  transition: color 0.2s;
}
.comments-close:hover {
  color: #ff6a00;
}
.comments-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 0;
  color: #999;
  font-size: 13px;
}
.comments-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.comments-empty {
  padding: 24px 0;
  text-align: center;
  color: #bbb;
  font-size: 13px;
}
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.comment-item {
  display: flex;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}
.comment-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.comment-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
}
.comment-body {
  flex: 1;
  min-width: 0;
}
.comment-author-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.comment-author {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}
.comment-tag-self {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 6px;
  border-radius: 4px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}
.comment-content {
  margin: 0 0 6px;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  word-break: break-word;
}
.comment-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.comment-time {
  font-size: 11px;
  color: #bbb;
}
.comment-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.comment-action {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: none;
  border: none;
  font-size: 12px;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}
.comment-action:hover {
  background: #fff;
}
.comment-action.like:hover {
  color: #ff4757;
}
.comment-action.like:hover svg {
  stroke: #ff4757;
}
.comment-action.like.liked {
  color: #ff4757;
}
.comment-action.like.liked svg {
  fill: #ff4757;
  stroke: #ff4757;
}
.comment-action.delete:hover {
  color: #ef4444;
  background: rgba(239,68,68,0.06);
}
.comment-action.delete:hover svg {
  stroke: #ef4444;
}
.comments-load-more {
  padding: 12px 0 4px;
  text-align: center;
  color: #ff9600;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.2s;
}
.comments-load-more:hover {
  color: #ff6a00;
}

/* 评论输入框 */
.comment-input-area {
  display: flex;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}
.comment-input-area input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 13px;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: all 0.25s;
}
.comment-input-area input:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255,150,0,0.1);
}
.comment-input-area input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}
.comment-submit-btn {
  padding: 0 20px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(255,106,0,0.2);
}
.comment-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255,106,0,0.3);
}
.comment-submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.empty-tip {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
}
.empty-icon {
  margin-bottom: 16px;
  color: #d1d5db;
}
.empty-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 6px;
  font-weight: 500;
}
.empty-sub {
  font-size: 13px;
  color: #bbb;
  margin: 0;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: #999;
  font-size: 14px;
}
.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px;
  color: #ff9600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}
.load-more:hover {
  color: #ff7d00;
  gap: 10px;
}
.load-end {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px;
}
.load-end-line {
  width: 40px;
  height: 1px;
  background: #e2e8f0;
}
.load-end-text {
  color: #ccc;
  font-size: 12px;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.preview-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.3s;
}
.preview-close:hover {
  background: rgba(255,255,255,0.2);
}
.image-preview-big {
  max-width: 90%;
  max-height: 80%;
  object-fit: contain;
  border-radius: 8px;
}
.image-preview-nav {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  color: #fff;
  font-size: 14px;
}
.nav-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.12);
  border-radius: 50%;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
}
.nav-btn:hover {
  background: rgba(255,255,255,0.25);
  transform: scale(1.05);
}
.nav-counter {
  min-width: 60px;
  text-align: center;
  font-weight: 500;
}
</style>
